<template>
  <LModal
    v-if="showStartDialog"
    v-model:visible="visible"
    :title="t('modules.sqdc.components.workflow.SQDCWorkflowParallelStartDialog.title')"
    size="xl"
    :loading="commitLoading"
    :show-footer="true"
  >
    <div class="parallel-start" :aria-busy="commitLoading">
      <p class="text-color-secondary" aria-live="polite">
        {{
          t("modules.sqdc.components.workflow.SQDCWorkflowParallelStartDialog.progress", {
            completed: completedCount,
            total: drafts.length,
          })
        }}
      </p>

      <div v-if="errorMessage" class="parallel-start__error" role="alert">
        {{ errorMessage }}
      </div>

      <FormPanel
        v-for="draft in drafts"
        :key="draft.entry.entryKey"
        class="parallel-start__entry"
        :aria-label="entryPath(draft.entry)"
      >
        <div>
          <strong>{{ t(`dynamic.${draft.entry.eventActionType.nameId}`) }}</strong>
          <div class="text-color-secondary text-sm">
            {{ entryPath(draft.entry) }}
          </div>
        </div>
        <div class="parallel-start__entry-actions">
          <Tag
            :value="entryStatusLabel(draft)"
            :severity="draft.input == null ? 'warn' : 'success'"
          />
          <LISAButton
            severity="secondary"
            outlined
            :label="
              t(
                draft.input == null
                  ? 'modules.sqdc.components.workflow.SQDCWorkflowParallelStartDialog.complete'
                  : 'modules.sqdc.components.workflow.SQDCWorkflowParallelStartDialog.edit',
              )
            "
            @click="edit(draft)"
          />
        </div>
      </FormPanel>
    </div>

    <template #footer="{ close }">
      <div class="parallel-start__footer">
        <LISAButton severity="secondary" :disabled="commitLoading" @click="close">
          {{ t("global.cancel") }}
        </LISAButton>
        <LISAButton
          severity="success"
          :disabled="!allValid || commitLoading"
          :loading="commitLoading"
          @click="commit"
        >
          {{ t("modules.sqdc.components.workflow.SQDCWorkflowParallelStartDialog.startAll") }}
        </LISAButton>
      </div>
    </template>
  </LModal>

  <SQDCModalEvent
    v-else-if="editingDraft != null && editingDraft.entry.nodeType === 'EVENT'"
    v-model:visible="entryEditorVisible"
    :event-type="editingDraft.entry.eventActionType as ModalEventType"
    :default-event="editingDraft.eventModel"
    :site-id="siteId"
    :board-id="eventBoardId(editingDraft)"
    :responsible-role-label="editingResponsibleRoleLabel"
    :user-field-option-ids="editingUserFieldOptionIds"
    :user-field-role-labels="editingUserFieldRoleLabels"
    :persist-on-save="false"
    @draft-save="saveEventDraft"
  />
  <SQDCModalAction
    v-else-if="editingDraft != null"
    v-model:visible="entryEditorVisible"
    :action-type="editingDraft.entry.eventActionType as ModalEventType"
    :default-action="editingDraft.actionModel"
    :event-id="preparation.currentEventId ?? undefined"
    :site-id="siteId"
    :board-id="boardId ?? undefined"
    :responsible-role-label="editingResponsibleRoleLabel"
    :user-field-option-ids="editingUserFieldOptionIds"
    :user-field-role-labels="editingUserFieldRoleLabels"
    :persist-on-save="false"
    @draft-save="saveActionDraft"
  />
</template>

<script setup lang="ts">
import LModal from "@/components/LModal.vue";
import LISAButton from "@/components/shared/primevue/LISAButton.vue";
import FormPanel from "@/components/shared/form/FormPanel.vue";
import type {
  ISQDCActionCreateInput,
  ISQDCEventCreateInput,
  SQDCWorkflowParallelCommitMutation,
  SQDCWorkflowParallelPrepareQuery,
} from "@/graphql";
import {
  UserFunctionScope,
  useSQDCWorkflowParallelCommitMutation,
  useUserFunctionsQuery,
} from "@/graphql";
import Tag from "primevue/tag";
import { computed, defineAsyncComponent, ref, watch } from "vue";
import { useTranslation } from "@/language/translation";
import {
  createDefaultActionForWorkflow,
  createDefaultActionFromShorthand,
  createDefaultEventForWorkflow,
  createDefaultEventFromShorthand,
  getShorthandUserFieldOptionIds,
  getShorthandUserFieldFunctionOverlayIds,
  getShorthandUserFunctionIds,
} from "../eventActionBoard/modals/SQDCShorthandDefaultHelper";
import type {
  ModalActionEditType,
  ModalEventEditType,
  ModalEventType,
} from "../eventActionBoard/modals/SQDCModelHelper";
import type { SQDCWorkflowDefinition } from "../../workflow/domain";
import {
  createSQDCWorkflowParallelCommitEntries,
  createSQDCWorkflowParallelDraftInput,
  getSQDCWorkflowParallelDraftProgress,
  getSQDCWorkflowParallelEntryPath,
} from "../../workflow/parallelStartState";

const SQDCModalAction = defineAsyncComponent(
  () => import("../eventActionBoard/modals/SQDCModalAction.vue"),
);
const SQDCModalEvent = defineAsyncComponent(
  () => import("../eventActionBoard/modals/SQDCModalEvent.vue"),
);

type Preparation = SQDCWorkflowParallelPrepareQuery["SQDCWorkflowParallelPrepare"];
type PreparationEntry = Preparation["entries"][number];
type DraftInput = { event?: ISQDCEventCreateInput; action?: ISQDCActionCreateInput };
type EntryDraft = {
  entry: PreparationEntry;
  input?: DraftInput;
  eventModel?: ModalEventEditType;
  actionModel?: ModalActionEditType;
};

const visible = defineModel("visible", { type: Boolean, required: true });
const props = defineProps<{
  preparation: Preparation;
  definition: SQDCWorkflowDefinition;
  siteId: number;
  boardId?: number | null;
  transitionComment?: string;
  selectedDatapointId?: number;
}>();
const emit = defineEmits<{
  committed: [result: SQDCWorkflowParallelCommitMutation["SQDCWorkflowParallelCommit"]];
}>();
const { t } = useTranslation();
const { mutate: commitParallel, loading: commitLoading } = useSQDCWorkflowParallelCommitMutation();
const drafts = ref<EntryDraft[]>([]);
const editingDraft = ref<EntryDraft>();
const entryEditorVisible = ref(false);
const errorMessage = ref("");

watch(
  () => props.preparation,
  (preparation) => {
    drafts.value = preparation.entries.map(createDraft);
    editingDraft.value = undefined;
    errorMessage.value = "";
  },
  { immediate: true },
);
watch(entryEditorVisible, (isVisible) => {
  if (!isVisible) editingDraft.value = undefined;
});

const progress = computed(() => getSQDCWorkflowParallelDraftProgress(drafts.value));
const completedCount = computed(() => progress.value.completed);
const allValid = computed(() => progress.value.valid);
const showStartDialog = computed(() => editingDraft.value == null && visible.value);
const editingResponsibleRoleLabel = computed(() => {
  const shorthand = editingDraft.value?.entry.shorthand;
  if (shorthand?.overlayResponsibleRole !== true) return undefined;
  if (shorthand.responsible === "USER_FUNCTION" && shorthand.ResponsibleFunction?.nameId != null) {
    return String(t(`dynamic.${shorthand.ResponsibleFunction.nameId}`));
  }
  if (shorthand.responsible === "LAST_WORKFLOW_USER") {
    return String(t("modules.sqdc.components.EABWokflow.EABShorthandForm.lastWorkflowUser"));
  }
  return undefined;
});
const editingUserFieldOptionIds = computed(() =>
  getShorthandUserFieldOptionIds(editingDraft.value?.entry.shorthand as never),
);
const editingUserFunctionIds = computed(() =>
  getShorthandUserFunctionIds(editingDraft.value?.entry.shorthand as never),
);
const { result: editingUserFunctionsResult } = useUserFunctionsQuery(
  () => ({ scopes: [UserFunctionScope.SITE], siteIds: [props.siteId] }),
  () => ({ enabled: editingUserFunctionIds.value.length > 0, fetchPolicy: "cache-first" }),
);
const editingUserFieldRoleLabels = computed(() => {
  const functionIds = getShorthandUserFieldFunctionOverlayIds(
    editingDraft.value?.entry.shorthand as never,
  );
  return Object.fromEntries(
    Object.entries(functionIds).flatMap(([fieldId, functionId]) => {
      const userFunction = editingUserFunctionsResult.value?.UserFunctions.find(
        (candidate) => candidate.id === functionId,
      );
      return userFunction == null
        ? []
        : [[Number(fieldId), String(t(`dynamic.${userFunction.nameId}`))] as const];
    }),
  );
});

function createDraft(entry: PreparationEntry): EntryDraft {
  const initial = structuredClone(entry.initialValues) as Record<string, unknown>;
  const input = createSQDCWorkflowParallelDraftInput(entry);
  return {
    entry,
    input,
    eventModel: entry.nodeType === "EVENT" ? createEventModel(entry, initial) : undefined,
    actionModel: entry.nodeType === "ACTION" ? createActionModel(entry, initial) : undefined,
  };
}

function createEventModel(entry: PreparationEntry, values: Record<string, unknown>) {
  const model =
    entry.shorthand == null
      ? createDefaultEventForWorkflow()
      : createDefaultEventFromShorthand(entry.shorthand as never);
  return {
    ...model,
    creatorId: values.creatorId as number | undefined,
    occurrenceDate: values.occurrenceDate as string | undefined,
    eisenhowerPriority:
      (values.eisenhowerPriority as ModalEventEditType["eisenhowerPriority"] | undefined) ??
      model.eisenhowerPriority,
    flagIds: (values.flagIds as number[] | undefined) ?? model.flagIds,
    fileIds: (values.fileIds as number[] | undefined) ?? model.fileIds,
  };
}

function createActionModel(entry: PreparationEntry, values: Record<string, unknown>) {
  const model =
    entry.shorthand == null
      ? createDefaultActionForWorkflow()
      : createDefaultActionFromShorthand(entry.shorthand as never);
  return {
    ...model,
    assigneeId: values.assigneeId as number | undefined,
    deadline: values.deadline as string | undefined,
    status: (values.status as ModalActionEditType["status"] | undefined) ?? model.status,
    fileIds: (values.fileIds as number[] | undefined) ?? model.fileIds,
  };
}

function edit(draft: EntryDraft) {
  editingDraft.value = draft;
  entryEditorVisible.value = true;
}

function saveEventDraft(payload: { event: ModalEventEditType; config: ISQDCEventCreateInput }) {
  if (editingDraft.value == null) return;
  editingDraft.value.eventModel = payload.event;
  editingDraft.value.input = { event: payload.config };
  entryEditorVisible.value = false;
}

function saveActionDraft(payload: { action: ModalActionEditType; config: ISQDCActionCreateInput }) {
  if (editingDraft.value == null) return;
  editingDraft.value.actionModel = payload.action;
  editingDraft.value.input = { action: payload.config };
  entryEditorVisible.value = false;
}

function eventBoardId(draft: EntryDraft) {
  return (
    draft.input?.event?.boardId ??
    (draft.entry.initialValues as { boardId?: number }).boardId ??
    props.boardId ??
    undefined
  );
}

function entryPath(entry: PreparationEntry) {
  return getSQDCWorkflowParallelEntryPath(entry, props.definition)
    .map((node) => t(`modules.sqdc.components.workflow.SQDCWorkflowEditor.nodeTypes.${node.type}`))
    .join(" / ");
}

function entryStatusLabel(draft: EntryDraft) {
  if (draft.input != null) {
    return t(
      draft.entry.requiresUserInput
        ? "modules.sqdc.components.workflow.SQDCWorkflowParallelStartDialog.completeStatus"
        : "modules.sqdc.components.workflow.SQDCWorkflowParallelStartDialog.defaultedStatus",
    );
  }
  return t("modules.sqdc.components.workflow.SQDCWorkflowParallelStartDialog.requiredStatus");
}

async function commit(): Promise<boolean> {
  if (!allValid.value) return false;
  errorMessage.value = "";
  try {
    const result = await commitParallel({
      input: {
        workflowInstanceId: props.preparation.workflowInstanceId,
        currentStepId: props.preparation.currentStepId,
        definitionId: props.preparation.definitionId,
        sourceConnectionId: props.preparation.sourceConnectionId,
        parallelNodeId: props.preparation.parallelNodeId,
        preparationToken: props.preparation.preparationToken,
        transitionComment: props.transitionComment,
        selectedDatapointId: props.selectedDatapointId,
        entries: createSQDCWorkflowParallelCommitEntries(drafts.value),
      },
    });
    if (result?.data?.SQDCWorkflowParallelCommit == null) {
      throw new Error("Parallel workflow commit returned no result");
    }
    emit("committed", result.data.SQDCWorkflowParallelCommit);
    visible.value = false;
    return true;
  } catch (error) {
    const message = error instanceof Error ? error.message.toLowerCase() : "";
    errorMessage.value = t(
      message.includes("active") || message.includes("completed") || message.includes("advanced")
        ? "modules.sqdc.components.workflow.SQDCWorkflowParallelStartDialog.concurrencyError"
        : "modules.sqdc.components.workflow.SQDCWorkflowParallelStartDialog.validationError",
    );
    return false;
  }
}
</script>

<style scoped lang="scss">
.parallel-start {
  display: grid;
  gap: 0.75rem;
}

.parallel-start__entry {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.parallel-start__entry :deep(.form-panel__content) {
  display: contents;
}

.parallel-start__entry-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.parallel-start__error {
  padding: 0.75rem;
  color: var(--p-red-700);
  background: var(--p-red-50);
  border-radius: var(--p-content-border-radius);
}

.parallel-start__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

@media (max-width: 40rem) {
  .parallel-start__entry {
    align-items: stretch;
    flex-direction: column;
  }

  .parallel-start__entry-actions,
  .parallel-start__footer {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
